// React
import React, { useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { components } from 'react-select'

/**
 * Custom MenuList component that properly detects scroll events
 * from both scroll wheel/trackpad and manual scrollbar dragging.
 *
 * React-select's default onMenuScrollToBottom only works with wheel events,
 * not with manual scrollbar dragging. This component adds a scroll event listener
 * to detect both methods of scrolling.
 *
 * https://github.com/JedWatson/react-select/issues/3232
 * captureMenuScroll not working on our end.
 *
 * @param {object} props - Component props
 * @param {JSX.Element} props.children - Child components
 * @param {object} props.selectProps - Select component props
 *
 * @return {JSX.Element} - Rendered MenuList component
 */
const MenuList = (props) => {
  const { children, selectProps } = props
  const { onMenuScrollToBottom } = selectProps || {}
  const menuListRef = useRef(null)

  const handleScroll = useCallback((event) => {
    if (!onMenuScrollToBottom) return

    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = event.target

    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10

    if (isNearBottom) {
      onMenuScrollToBottom()
    }
  }, [onMenuScrollToBottom])

  useEffect(() => {
    const menuList = menuListRef.current
    if (menuList) {
      menuList.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        menuList.removeEventListener('scroll', handleScroll)
      }
    }
  }, [onMenuScrollToBottom])

  return (
    <components.MenuList {...props} innerRef={menuListRef}>
      {children}
    </components.MenuList>
  )
}

MenuList.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    onMenuScrollToBottom: PropTypes.any
  })
}

export { MenuList }
